if (!customElements.get('product-recommendations')) {
  customElements.define(
    'product-recommendations',
    class ProductRecommendations extends HTMLElement {
      constructor() {
        super();

        if ('requestIdleCallback' in window) {
          requestIdleCallback(this.init.bind(this), { timeout: 1500 });
        } else {
          FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '0px 0px 400px 0px' });
        }
      }

      connectedCallback() {
        if (this.dataset.mmPreview === 'true') this.initPreview();
      }

      initPreview() {
        if (!this.isConnected || this.previewReady) return;
        this.previewReady = true;
        this.previewGeneration = 0;
        this.bindPreviewModals();
        this.previewUnsubscribe = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          (event) => { if (!event.cart?.errors) this.refreshPreview(); }
        );
      }

      bindPreviewModals() {
        this.previewObserver?.disconnect();
        this.previewModals = Array.from(this.querySelectorAll('quick-view-modal'));
        this.previewObserver = new MutationObserver(() => {
          requestAnimationFrame(() => this.flushPreview());
        });
        this.previewObserver.observe(this, { childList: true, subtree: true });
        this.previewModals.forEach((modal) => this.previewObserver.observe(modal, { attributes: true, attributeFilter: ['hidden', 'open', 'inert'] }));
      }

      flushPreview() {
        const pending = this.pendingPreview;
        if (!pending || !this.isConnected || pending.generation !== this.previewGeneration) return;
        if (this.previewModals.some((modal) => !modal.hidden || !this.contains(modal))) return;
        const active = document.activeElement;
        const focused = this.contains(active) ? active.id : '';
        const hadFocus = this.contains(active);
        this.pendingPreview = null;
        this.previewObserver?.disconnect();
        this.innerHTML = pending.html;
        this.hidden = false;
        this.bindPreviewModals();
        if (hadFocus && document.activeElement === document.body) {
          const target = focused ? this.querySelector(`#${CSS.escape(focused)}`) : null;
          const fallback = this.closest('product-info')?.querySelector('form[is="product-form"] button[name="add"]:not([disabled])') || this;
          (target || fallback).focus({ preventScroll: true });
        }
      }

      async refreshPreview() {
        const generation = ++this.previewGeneration;
        this.previewAbort?.abort();
        const controller = new AbortController();
        this.previewAbort = controller;
        const timeout = setTimeout(() => controller.abort(), 10000);
        try {
          const response = await fetch(this.dataset.url, { signal: controller.signal, credentials: 'same-origin', cache: 'no-store' });
          if (!response.ok) throw new Error('Recommendation section unavailable');
          const html = await response.text();
          if (!this.isConnected || generation !== this.previewGeneration) return;
          const replacement = new DOMParser().parseFromString(html, 'text/html').getElementById(this.id);
          if (!replacement) throw new Error('Recommendation section missing');
          this.pendingPreview = { generation, html: replacement.innerHTML };
          this.flushPreview();
        } catch (error) {
          if (this.isConnected && generation === this.previewGeneration) {
            this.pendingPreview = { generation, html: '' };
            this.flushPreview();
          }
        } finally {
          clearTimeout(timeout);
        }
      }

      disconnectedCallback() {
        this.previewAbort?.abort();
        this.previewUnsubscribe?.();
        this.previewObserver?.disconnect();
        this.pendingPreview = null;
        this.previewReady = false;
      }

      init() {
        if (this.dataset.mmPreview === 'true') { this.initPreview(); return; }
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((responseText) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(responseText, 'text/html')
              .querySelector('.shopify-section');

            if (sectionInnerHTML === null) return;

            const recommendations = sectionInnerHTML.querySelector('product-recommendations');
            if (recommendations && recommendations.innerHTML.trim().length) {
              this.innerHTML = recommendations.innerHTML;
            }

            if (recommendations.querySelector('.product-card')) {
              this.dispatchEvent(new CustomEvent('recommendations:loaded'));
            } else {
              if (recommendations.classList.contains('complementary-products')) {
                this.closest('.product__block')?.remove();
              } else {
                this.closest('.shopify-section')?.remove();
              }
              this.dispatchEvent(new CustomEvent('recommendations:empty'));
            }
          })
          .catch((e) => {
            console.error(e);
          });
      }
    }
  );
}
